import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { getJSON, postJSON } from '../../utils/api';

export default function StartChatDialog({ open, onClose, onCreated }) {
  const [query, setQuery] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setErr(null);
      setBusy(false);
      setResults([]);
      setSelectedId(null);
    }
  }, [open]);

  const searchUsers = async (value) => {
    setQuery(value);
    setErr(null);
    setSelectedId(null);

    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }

    try {
      const res = await getJSON(`/api/users/search?q=${encodeURIComponent(trimmed)}`);
      setResults(res || []);
    } catch (e) {
      setErr('Failed to search users.');
      setResults([]);
    }
  };

  const submit = async (userIdOverride) => {
    const userId = userIdOverride || selectedId;
    if (!userId) return;

    setErr(null);
    setBusy(true);
    try {
      const conv = await postJSON('/api/chats/start', { user_id: userId });
      onCreated?.(conv);
      setQuery('');
      setResults([]);
      setSelectedId(null);
      onClose?.();
    } catch (e) {
      const msg = (e && e.message) ? e.message : 'Could not start chat.';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Start a new chat</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
        <TextField
          autoFocus
          label="Search by username"
          fullWidth
          value={query}
          onChange={e => searchUsers(e.target.value)}
          placeholder="Start typing a username…"
        />

        {results.length > 0 && (
          <List dense sx={{ mt: 1 }}>
            {results.map(u => (
              <ListItemButton
                key={u.id}
                selected={selectedId === u.id}
                onClick={() => {
                  setSelectedId(u.id);
                  submit(u.id); // start chat immediately on click
                }}
              >
                <ListItemText
                  primary={u.username || 'Unknown user'}
                  secondary={
                    u.role ? (
                      <Typography variant="caption" color="text.secondary">
                        {u.role}
                      </Typography>
                    ) : null
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {query.trim().length >= 2 && results.length === 0 && !err && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No users found with that username.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button
          variant="contained"
          onClick={() => submit()}
          disabled={busy || !selectedId}
        >
          Start
        </Button>
      </DialogActions>
    </Dialog>
  );
}